import { uniqBy, keyBy } from "lodash";
import {
  REQUEST_BATCHES_SUCCESS,
  REQUEST_BATCH_DETAILS_SUCCESS,
  REQUEST_BATCH_ITEMS_SUCCESS,
  REQUEST_BATCH_BARCODES_SUCCESS,
  REQUEST_BATCH_ITEMS_COUNT_SUCCESS,
  UNBATCH_ENTIRE_BATCH_SUCCESS,
  ARCHIVE_BATCH_SUCCESS,
  UNARCHIVE_BATCH_SUCCESS,
  UPDATE_BATCH_SUCCESS,
  READY_TO_SHIP_SUCCESS,
} from "../../constants/actionTypes";

const INITIAL_STATE = {
  batches: [],
  error: null,
  batchDetails: {},
  batchItems: {},
  batchBarcodes: {},
  batchOrders: {},
  printingInfo: {},
  products: {},
  files: {},
  // Survives the 60s/5m GET read-through cache so QC pass flips the
  // batch pill and item page immediately, even if the list refetch is stale.
  qcFloorStamps: {},
};

function applyQcFloorStamps(items, stamps) {
  if (!stamps || !items?.length) return items;
  return items.map((it) => {
    const stamp = stamps[String(it?.id || "")];
    return stamp ? { ...it, ...stamp } : it;
  });
}

export default function batches(state = INITIAL_STATE, action) {
  const {
    data = {},
    id: batchId,

    refreshList = false,
    batch = {},
  } = action?.payload || {};

  switch (action.type) {
    case REQUEST_BATCHES_SUCCESS:
      const { batches = [] } = data;
      let newBatches = [];

      if (refreshList) {
        newBatches = batches;
      } else {
        newBatches = [...state.batches, ...batches];
        newBatches = uniqBy(newBatches, (x) => x.id);
      }

      newBatches = newBatches.sort(
        (a, b) => b?.createdAt?._seconds - a?.createdAt?._seconds
      );

      return {
        ...state,
        batches: newBatches,
        batchDetails: {},
        error: null,
      };

    case REQUEST_BATCH_DETAILS_SUCCESS:
      const updatedBatchDetails = {
        ...state.batchDetails,
        [batch?.id]: batch,
      };

      return {
        ...state,
        batchDetails: updatedBatchDetails,
      };

    case REQUEST_BATCH_ITEMS_SUCCESS: {
      const {
        items = [],
        orders = [],
        printingInfo = [],
        products = [],
        files = [],
      } = data;
      const append = Boolean(action?.payload?.append);
      const prevItems = append ? state.batchItems[batchId] || [] : [];
      const mergedItems = applyQcFloorStamps(
        append ? uniqBy([...prevItems, ...items], (x) => x?.id) : items,
        state.qcFloorStamps
      );

      const prevOrders = append ? state.batchOrders[batchId] || {} : {};
      const orderList = (Array.isArray(orders) ? orders : []).filter(
        (o) => o && (o.id || o.orderId)
      );
      const nextOrders = {
        ...prevOrders,
        ...keyBy(orderList, (o) => o.id || o.orderId),
      };

      const prevPrinting = append ? state.printingInfo[batchId] || {} : {};
      const nextPrinting =
        printingInfo?.length > 0
          ? { ...prevPrinting, ...keyBy(printingInfo, "id") }
          : prevPrinting;

      const prevProducts = append ? state.products[batchId] || {} : {};
      const nextProducts =
        products?.length > 0
          ? {
              ...prevProducts,
              ...keyBy(products, (x) => x?.product?.id),
            }
          : prevProducts;

      const prevFiles = append ? state.files[batchId] || {} : {};
      const nextFiles =
        files?.length > 0
          ? { ...prevFiles, ...keyBy(files, "id") }
          : prevFiles;

      return {
        ...state,
        batchItems: {
          ...state.batchItems,
          [batchId]: mergedItems,
        },
        batchOrders: {
          ...state.batchOrders,
          [batchId]: nextOrders,
        },
        printingInfo: {
          ...state.printingInfo,
          [batchId]: nextPrinting,
        },
        products: {
          ...state.products,
          [batchId]: nextProducts,
        },
        files: {
          ...state.files,
          [batchId]: nextFiles,
        },
        error: null,
      };
    }

    case REQUEST_BATCH_BARCODES_SUCCESS:
      const { barcodes = [] } = data;
      const updatedBatchBarcodes = {
        ...state.batchBarcodes,
        [batchId]: barcodes,
      };

      return {
        ...state,
        batchBarcodes: updatedBatchBarcodes,
      };

    case REQUEST_BATCH_ITEMS_COUNT_SUCCESS:
      return {
        ...state,
      };

    case UNBATCH_ENTIRE_BATCH_SUCCESS:
      const newBatchList =
        state.batches.filter((batch) => batch?.id !== action?.payload) || [];
      return {
        ...state,
        batches: newBatchList,
      };

    case ARCHIVE_BATCH_SUCCESS:
      return {
        ...state,
        batches: state.batches.filter((batch) => batch.id !== action?.payload),
      };

    case UNARCHIVE_BATCH_SUCCESS:
      return {
        ...state,
        batches: state.batches.map((batch) =>
          batch.id === action?.payload
            ? { ...batch, archived: false }
            : batch
        ),
      };

    case READY_TO_SHIP_SUCCESS: {
      const { data, id: itemId } = action?.payload || {};
      const next = data?.batchItem || {};
      const matchId = String(next.id || itemId || "");
      const binId = data?.bin?.id || data?.bin?.binId || next.binId;
      if (!matchId) return state;
      const patch = {
        fulfilled: true,
        ...(binId ? { binId } : {}),
        ...(next.qcPassedAt ? { qcPassedAt: next.qcPassedAt } : {}),
        ...(next.qcPassedBy ? { qcPassedBy: next.qcPassedBy } : {}),
        ...(next.qcPassedByName ? { qcPassedByName: next.qcPassedByName } : {}),
      };
      const qcFloorStamps = {
        ...(state.qcFloorStamps || {}),
        [matchId]: {
          ...(state.qcFloorStamps || {})[matchId],
          ...patch,
        },
      };
      let changed = false;
      const batchItems = Object.fromEntries(
        Object.entries(state.batchItems || {}).map(([bid, items]) => [
          bid,
          (items || []).map((it) => {
            if (String(it?.id) !== matchId) return it;
            changed = true;
            return { ...it, ...patch };
          }),
        ])
      );
      return {
        ...state,
        qcFloorStamps,
        ...(changed ? { batchItems } : {}),
      };
    }

    case UPDATE_BATCH_SUCCESS: {
      const { id, batch } = action?.payload || {};
      if (!id || !batch) return state;
      const patch = {
        barcodesDownloaded: batch.barcodesDownloaded,
        barcodePrintTracked: batch.barcodePrintTracked,
        blanksAwaitingBarcodePrint: batch.blanksAwaitingBarcodePrint,
        needsBarcodePrint: Boolean(batch.blanksAwaitingBarcodePrint),
        lastPrintRun: batch.lastPrintRun || null,
        printRunCount:
          typeof batch.printRunCount === "number" ? batch.printRunCount : 0,
        barcodePrintedCount:
          typeof batch.barcodePrintedCount === "number"
            ? batch.barcodePrintedCount
            : null,
        barcodeWaitingCount:
          typeof batch.barcodeWaitingCount === "number"
            ? batch.barcodeWaitingCount
            : null,
      };
      return {
        ...state,
        batches: state.batches.map((b) =>
          b.id === id ? { ...b, ...patch } : b
        ),
        batchDetails: {
          ...state.batchDetails,
          [id]: {
            ...(state.batchDetails[id] || {}),
            ...batch,
          },
        },
      };
    }

    default:
      return state;
  }
}
